import React from "react";
import styled, { css } from "styled-components/macro";
import { Paper } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Trans } from "react-i18next";
import HowLearningWorksDialog from "./HowLearningWorksDialog";
import { useToggle } from "react-use";

const Wrapper = styled(Paper)<{ $completed: boolean }>`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  font-family: "Noto Sans", sans-serif;
  background: ${({ theme }) => theme.duo.palette.white};
  border-radius: 16px;
  ${({ $completed, theme }) => css`
    border: ${$completed ? `1px solid ${theme.duo.palette.greenMedium}` : "0px"};
  `}
`;
const Stat = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 86px;
`;

const Divider = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.duo.palette.blueLighter};
  margin: 20px 0;
`;

const Title = styled.div`
  font-size: ${({ theme }) => theme.duo.fontSize.smaller};
  font-weight: ${({ theme }) => theme.duo.fontWeight.w400};
  color: ${({ theme }) => theme.duo.palette.greyDark};
  text-transform: uppercase;
`;

const Number = styled.div`
  font-size: ${({ theme }) => theme.duo.fontSize.large};
  font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
  color: ${({ theme }) => theme.duo.color.text};
`;

const DoneIcon = styled(CheckCircleIcon)`
  margin: 6px 0 3px;
  font-size: ${({ theme }) => theme.duo.fontSize.large};
  color: ${({ theme }) => theme.duo.color.green};
`;

interface DeckStats {
  unknown: number;
  known: number;
  completed: number;
}

interface CardStatsProps {
  stats: DeckStats;
  className?: string;
}

const CardStats: React.FC<CardStatsProps> = ({ stats, className }) => {
  const { unknown, known, completed } = stats;
  const [popupOpen, togglePopupOpen] = useToggle(false);

  const noneToLearn = unknown === 0;

  return (
    <>
      <Wrapper className={className} $completed={noneToLearn} onClick={togglePopupOpen}>
        <Stat>
          <Title>
            <Trans>To learn</Trans>
          </Title>
          {noneToLearn ? <DoneIcon /> : <Number>{unknown}</Number>}
        </Stat>
        <Divider />
        <Stat>
          <Title>
            <Trans>Practiced</Trans>
          </Title>
          <Number>{known}</Number>
        </Stat>
        <Divider />
        <Stat>
          <Title>
            <Trans>Learned</Trans>
          </Title>
          <Number>{completed}</Number>
        </Stat>
      </Wrapper>
      <HowLearningWorksDialog open={popupOpen} onClose={togglePopupOpen} />
    </>
  );
};

export default CardStats;
